import { useCallback, useEffect, useState } from 'react'
import { useCreateModuleCommentMutation, useDeleteModuleCommentMutation, useGetModuleCommentsQuery, useUpdateModuleCommentMutation } from '@app/features/other/moduleCommentsApiSlice';
import { Thread } from './CommentsAction';
import useErrorMessage from '@utils/useErrorMessage';

const useComments = ({ threadId }: { threadId: number }) => {
	const { data: thread, isLoading } = useGetModuleCommentsQuery({ threadId });
	const [createComment, { isLoading: isCommentLoading, isError: isCreateCommentError, error: createCommentError }] =
		useCreateModuleCommentMutation();
	const [updateComment, { isLoading: isCommentUpdating, isError: isUpdateCommentError, error: updateCommentError }] = 
		useUpdateModuleCommentMutation();
	const [deleteComment, { isLoading: isCommentDeleting, isError: isDeleteCommentError, error: deleteCommentError }] =
		useDeleteModuleCommentMutation();
	const [isCommentActive, setIsCommentActive] = useState<boolean>(false);

	const handleCreateComment = useCallback(
		({ content }: { content: string }) => {
			createComment({ threadId, content, parent: null });
		},
		[createComment, threadId],
	);

	const handleCreateReply = useCallback(
		({ content, parent }: { content: string; parent: number }) => {
			createComment({ threadId, content, parent });
		},
		[createComment, threadId],
	);

	const handleUpdateComment = useCallback(
		(commentId: number, content: string) => {
			updateComment({ threadId, commentId, content });
		},
		[updateComment, threadId],
	);

	const handleDeleteComment = useCallback(
		(id: number) => {
			deleteComment({ commentId: id });
		},
		[deleteComment],
	);

	useEffect(() => {
		if (isCommentActive) setIsCommentActive(false);
	}, [thread]);

	const { errorMsg: createErrorMsg } = useErrorMessage({ error: createCommentError, isError: isCreateCommentError });
	const { errorMsg: updateErrorMsg } = useErrorMessage({ error: updateCommentError, isError: isUpdateCommentError });
	const { errorMsg: deleteErrorMsg } = useErrorMessage({ error: deleteCommentError, isError: isDeleteCommentError });

	return {
		thread: thread ? new Thread(thread) : null,
		isLoading: isLoading || isCommentLoading || isCommentDeleting || isCommentUpdating,
		isCommentActive,
		setIsCommentActive,
		handleCreateComment,
		handleCreateReply,
		handleUpdateComment,
		handleDeleteComment,
		createErrorMsg,
		updateErrorMsg,
		deleteErrorMsg
	}
}

export default useComments